import {
  type LoadableState,
  useEffectWithController
} from '@carvana/merch-react-hooks';
import React from 'react';
import type { CustomizeRequest, CustomizeResponse } from '~/contracts/new-cars';
import fetchCustomizeOptions from '~/services/new-cars/fetchCustomizeOptions';
import { generateErrorLogger } from '~/utilities/loggers';

export class NoMatchingVehiclesError extends Error {
  constructor() {
    super('No matching vehicles found');
  }
}

type OwnArgs = {
  loadableState: LoadableState<CustomizeResponse>;
  request: CustomizeRequest | null;
  suppressNextRef?: React.MutableRefObject<boolean>;
};

export default function useGetCustomizeOptions({
  loadableState,
  request,
  suppressNextRef
}: OwnArgs) {
  const { setData, setError, setLoading } = loadableState;

  useEffectWithController(
    controller => {
      if (!request) return;

      if (suppressNextRef?.current) {
        suppressNextRef.current = false;

        return;
      }

      setLoading();

      fetchCustomizeOptions(request, controller)
        .then(data => ({
          ...data,
          driveTypes: [...data.driveTypes].sort(
            (a, b) => a.priceAdd - b.priceAdd
          ),
          engines: [...(data.engines ?? [])].sort(
            (a, b) => a.priceAdd - b.priceAdd
          ),
          exteriorColors: [...data.exteriorColors].sort((a, b) =>
            a.name.localeCompare(b.name)
          ),
          interiorColors: [...data.interiorColors].sort((a, b) =>
            a.name.localeCompare(b.name)
          ),
          packageOptions: [...data.packageOptions].sort((a, b) => {
            if (a.code === 'NONE') return -1;
            if (b.code === 'NONE') return 1;

            return b.priceAdd - a.priceAdd;
          })
        }))
        .then(setData)
        .catch(err => {
          const bodyText: string = err?.response?.data ?? err?.message ?? '';

          if (bodyText.includes('No matching vehicles found')) {
            setError(new NoMatchingVehiclesError());

            return;
          }

          generateErrorLogger(
            'Get Customize Options',
            { request },
            { callback: setError, controller }
          )(err);
        });
    },
    [request, setData, setError, setLoading, suppressNextRef]
  );
}
